"use client";

import React, {
  useContext,
  useEffect,
  useRef,
  useCallback,
  useSyncExternalStore,
  type ComponentType,
  type ReactNode,
  type ReactElement,
  Children,
  isValidElement,
} from "react";
import { EditorContext, NodeContext } from "../hooks/context";
import { RenderNode } from "./Frame";
import type { Node, CraftConfig } from "../state/types";
import { parseReactElement } from "../serialization/parse-react";

interface ElementProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  is: ComponentType<any>;
  id?: string;
  canvas?: boolean;
  children?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export function Element({ is: Component, id: slotId, canvas, children, ...rest }: ElementProps) {
  const ctx = useContext(EditorContext);
  const parentNodeId = useContext(NodeContext);

  // Unnamed slots must be unique per <Element /> instance to avoid collisions
  // when multiple children of the same component type are declared.
  const generatedSlotIdRef = useRef<string | null>(null);
  if (!generatedSlotIdRef.current) {
    generatedSlotIdRef.current = `slot-${Component.displayName || Component.name || "unknown"}-${Math.random().toString(36).slice(2, 8)}`;
  }

  if (!ctx || !parentNodeId) {
    return <Component {...rest}>{children}</Component>;
  }

  const resolvedSlotId = slotId || generatedSlotIdRef.current!;

  return (
    <ElementInner
      Component={Component}
      slotId={resolvedSlotId}
      canvas={canvas}
      parentNodeId={parentNodeId}
      rest={rest}
    >
      {children}
    </ElementInner>
  );
}

(Element as unknown as { __builderElement?: true }).__builderElement = true;
Element.displayName = "Element";

function ElementInner({
  Component,
  slotId,
  canvas,
  parentNodeId,
  rest,
  children,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  slotId: string;
  canvas?: boolean;
  parentNodeId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rest: Record<string, any>;
  children?: ReactNode;
}) {
  const ctx = useContext(EditorContext)!;
  const { store } = ctx;

  const cachedLinkedIdRef = useRef<string | null>(null);

  // Subscribe to the linked node ID for this slot
  const linkedNodeId = useSyncExternalStore(
    store.subscribe,
    useCallback(() => {
      const parent = store.getState().nodes[parentNodeId];
      const id = parent?.data.linkedNodes[slotId] ?? null;
      // Avoid returning a new value if it's the same string
      if (id === cachedLinkedIdRef.current) return cachedLinkedIdRef.current;
      cachedLinkedIdRef.current = id;
      return id;
    }, [store, parentNodeId, slotId]),
    useCallback(() => {
      const parent = store.getState().nodes[parentNodeId];
      return parent?.data.linkedNodes[slotId] ?? null;
    }, [store, parentNodeId, slotId]),
  );

  // Stable ref for rest props — only used when creating a new linked node,
  // so we don't want rest identity changes to re-trigger the effect.
  const restRef = useRef(rest);
  restRef.current = rest;
  const childrenRef = useRef(children);
  childrenRef.current = children;

  function parseChildNodeTrees(parentId: string): {
    childNodeIds: string[];
    childNodes: Record<string, Node>;
  } {
    const childElements = collectValidChildElements(childrenRef.current);
    if (childElements.length === 0) {
      return { childNodeIds: [], childNodes: {} };
    }

    const childNodeIds: string[] = [];
    const childNodes: Record<string, Node> = {};

    for (const childElement of childElements) {
      const childTree = parseReactElement(childElement);
      childNodeIds.push(childTree.rootNodeId);

      for (const [id, node] of Object.entries(childTree.nodes)) {
        childNodes[id] = node;
      }
    }

    // Re-parent each root child to the linked node we are creating/updating.
    for (const childRootId of childNodeIds) {
      const root = childNodes[childRootId];
      if (!root) continue;
      childNodes[childRootId] = {
        ...root,
        data: {
          ...root.data,
          parent: parentId,
        },
      };
    }

    return { childNodeIds, childNodes };
  }

  // Create linked node if it doesn't exist. If it already exists but was created
  // without static child trees (old behavior), backfill children once.
  useEffect(() => {
    const state = store.getState();
    const parent = state.nodes[parentNodeId];
    if (!parent) return;

    if (linkedNodeId) {
      const existingLinkedNode = state.nodes[linkedNodeId];
      if (!existingLinkedNode) return;
      if (existingLinkedNode.data.nodes.length > 0) return;

      const { childNodeIds, childNodes } = parseChildNodeTrees(linkedNodeId);
      if (childNodeIds.length === 0) return;

      store.setState({
        ...state,
        contentRevision: state.contentRevision + 1,
        nodes: {
          ...state.nodes,
          ...childNodes,
          [linkedNodeId]: {
            ...existingLinkedNode,
            data: {
              ...existingLinkedNode.data,
              nodes: childNodeIds,
            },
          },
        },
      });
      return;
    }

    // Double-check it wasn't created between render and effect.
    if (parent.data.linkedNodes[slotId]) return;

    const newId = `linked-${parentNodeId}-${slotId}`;
    const craft = (Component as unknown as { craft?: CraftConfig }).craft;
    const currentRest = restRef.current;

    // If a node with the expected ID already exists in the store (e.g. an
    // orphaned linked child whose reference was stripped during shared-section
    // normalization), re-link it instead of overwriting it with default props.
    const existingOrphan = state.nodes[newId];
    if (existingOrphan) {
      const updatedParent = {
        ...parent,
        data: {
          ...parent.data,
          linkedNodes: { ...parent.data.linkedNodes, [slotId]: newId },
        },
      };
      // Re-parent the orphan to this node and restore the link.
      const relinkedOrphan = existingOrphan.data.parent === parentNodeId
        ? existingOrphan
        : {
            ...existingOrphan,
            data: { ...existingOrphan.data, parent: parentNodeId },
          };
      store.setState({
        ...state,
        contentRevision: state.contentRevision + 1,
        nodes: {
          ...state.nodes,
          [newId]: relinkedOrphan,
          [parentNodeId]: updatedParent,
        },
      });
      return;
    }

    const { childNodeIds, childNodes } = parseChildNodeTrees(newId);
    const displayName =
      craft?.displayName ?? (currentRest.displayName as string) ?? slotId;

    const newNode: Node = {
      id: newId,
      dom: null,
      data: {
        type: Component,
        isCanvas: canvas ?? craft?.canvas ?? false,
        props: { ...(craft?.props ?? {}), ...currentRest },
        displayName,
        name: displayName,
        custom: craft?.custom ?? {},
        parent: parentNodeId,
        nodes: childNodeIds,
        linkedNodes: {},
        hidden: false,
      },
      events: { selected: false, hovered: false, dragged: false },
      related: craft?.related ?? {},
      rules: {
        canDrag: (craft?.rules?.canDrag as Node["rules"]["canDrag"]) ?? (() => true),
        canDrop: (craft?.rules?.canDrop as Node["rules"]["canDrop"]) ?? (() => true),
        canMoveIn: (craft?.rules?.canMoveIn as Node["rules"]["canMoveIn"]) ?? (() => true),
        canMoveOut: (craft?.rules?.canMoveOut as Node["rules"]["canMoveOut"]) ?? (() => true),
      },
    };

    // Lightweight state update instead of Immer produce
    const updatedParent = {
      ...parent,
      data: {
        ...parent.data,
        linkedNodes: { ...parent.data.linkedNodes, [slotId]: newId },
      },
    };
    store.setState({
      ...state,
      contentRevision: state.contentRevision + 1,
      nodes: {
        ...state.nodes,
        ...childNodes,
        [newId]: newNode,
        [parentNodeId]: updatedParent,
      },
    });
  }, [linkedNodeId, store, parentNodeId, slotId, Component, canvas]);

  if (!linkedNodeId) {
    return null;
  }

  return <RenderNode nodeId={linkedNodeId} />;
}

function collectValidChildElements(children: ReactNode): ReactElement[] {
  const elements: ReactElement[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    if (child.type === React.Fragment) {
      const fragmentChildren = (child.props as { children?: ReactNode }).children;
      elements.push(...collectValidChildElements(fragmentChildren));
      return;
    }

    elements.push(child);
  });

  return elements;
}
